import { Icon } from "@mdi/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiContentCopy } from "@mdi/js";
import { Alert, Button, Divider, Popover, Space, Typography } from "antd";
import { DEFAULT_OPACITY } from "constants/chart.constants";
import { useChartsContext } from "contexts";
import debounce from "lodash/debounce";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

import { IBestFitSetting } from "models/bestFitToggle";
import { RegressionType } from "models/regressionType";

import { BaseRadioInput, LabeledSwitch } from "components/base";
import { ColorEditor } from "components/shared";

const BestFitOptions: React.FC = () => {
  // hooks
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const { bestFitToggle, apiResponse } = useChartEntities(id);
  // state
  const [copiedField, setCopiedField] = useState<"equation" | "rSquared" | null>(null);

  // derived data
  const isActive = bestFitToggle?.active;
  const { regressionType, forceRegressionThroughOrigin } =
    bestFitToggle?.properties || {};
  const equation = apiResponse?.series?.find((s) =>
    s.label.includes("Line of Best Fit")
  )?.equation;
  const rSquared = apiResponse?.series?.find((s) =>
    s.label.includes("Line of Best Fit")
  )?.rSquared;
  const copyDisabled = !equation || equation === "No equation available";

  const handleBestFitSettingsChange =
    (key: keyof IBestFitSetting) => (value: string | boolean | number) => {
      dispatch(
        updateChartByPath({
          id,
          path: `features.bestFitToggle.properties.${key}`,
          value
        })
      );
    };

  const handleToggle = (active: boolean) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.bestFitToggle.active",
        value: active
      })
    );
  };

  const handleForceRegressionThroughOrigin = (active: boolean) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.bestFitToggle.properties.forceRegressionThroughOrigin",
        value: active
      })
    );
  };

  const updateColorsDebounce = useMemo(
    () =>
      debounce((color: string, thickness: number) => {
        dispatch(
          updateChartByPath({
            id,
            path: "features.bestFitToggle.properties.color",
            value: color
          })
        );
        dispatch(
          updateChartByPath({
            id,
            path: "features.bestFitToggle.properties.thickness",
            value: thickness
          })
        );
      }, 200),
    [dispatch, id]
  );

  const handleOpacityChange = (opacity: number) => {
    dispatch(
      updateChartByPath({
        id,
        path: "features.bestFitToggle.properties.opacity",
        value: opacity
      })
    );
  };

  const handleCopy = useCallback((text: string, field: "equation" | "rSquared") => {
    navigator.clipboard.writeText(text).then(() => setCopiedField(field));
  }, []);

  useEffect(() => {
    if (copiedField) {
      const timeout = setTimeout(() => setCopiedField(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copiedField]);

  return (
    <Wrapper>
      <LabeledSwitch
        testId="bestFitSwitch"
        switch={{
          isChecked: isActive,
          onChange: handleToggle
        }}
        label={{
          value: "Line of Best Fit",
          isBold: true,
          isUppercase: true
        }}
      />
      {isActive && (
        <>
          <StyledDivider style={{ margin: "10px 0px 10px 0px" }} />
          <StyledSpace direction="vertical" split={<StyledDivider />}>
            <Row>
              <Typography.Text>Regression Type</Typography.Text>
            </Row>

            <RadioGroup>
              <BaseRadioInput
                label="Linear"
                name="regressionType"
                value="linear"
                modelValue={regressionType}
                onChange={() => handleBestFitSettingsChange("regressionType")("linear")}
              />
              <BaseRadioInput
                label="Exponential"
                name="regressionType"
                value="exponential"
                modelValue={regressionType}
                onChange={() =>
                  handleBestFitSettingsChange("regressionType")("exponential")
                }
              />
              <BaseRadioInput
                label="Logarithmic"
                name="regressionType"
                value="logarithmic"
                modelValue={regressionType}
                onChange={() =>
                  handleBestFitSettingsChange("regressionType")("logarithmic")
                }
              />
            </RadioGroup>
            <LabeledSwitch
              testId="regressionThroughOriginSwitch"
              switch={{
                isChecked: forceRegressionThroughOrigin,
                onChange: handleForceRegressionThroughOrigin,
                isDisabled: regressionType !== RegressionType.Linear
              }}
              label={{
                value: "Force Regression Through Origin"
              }}
            />
            <Row>
              <Typography.Text>Series Colour:</Typography.Text>
              <ColorEditor
                color={bestFitToggle?.properties?.color || "#000000"}
                thickness={bestFitToggle?.properties?.thickness || 2}
                opacity={{
                  value: bestFitToggle?.properties?.opacity ?? DEFAULT_OPACITY,
                  onChange: handleOpacityChange
                }}
                onChange={updateColorsDebounce}
                width={60}
              />
            </Row>
            <Row>
              <Typography.Text>Equation:</Typography.Text>{" "}
              <Typography.Text strong>
                {equation || "No equation available"}
              </Typography.Text>
              <Popover
                open={copiedField === "equation"}
                trigger="click"
                overlayClassName="overlay-no-padding"
                content={<Alert type="success" message="Copied to Clipboard!" />}>
                <CopyButton
                  data-testid="copy-equation-button"
                  onClick={() => handleCopy(equation, "equation")}
                  disabled={copyDisabled}>
                  <Icon path={mdiContentCopy} size={1.2} />
                </CopyButton>
              </Popover>
            </Row>
            <Row>
              <Typography.Text>R²:</Typography.Text>{" "}
              <Typography.Text strong>
                {rSquared?.toFixed(4) || "No R² available"}
              </Typography.Text>
              <Popover
                open={copiedField === "rSquared"}
                trigger="click"
                overlayClassName="overlay-no-padding"
                content={<Alert type="success" message="Copied to Clipboard!" />}>
                <CopyButton
                  data-testid="copy-rsquared-button"
                  onClick={() =>
                    handleCopy(rSquared != null ? rSquared.toString() : "", "rSquared")
                  }
                  disabled={copyDisabled}>
                  <Icon path={mdiContentCopy} size={1.2} />
                </CopyButton>
              </Popover>
            </Row>
          </StyledSpace>
        </>
      )}
    </Wrapper>
  );
};

export default BestFitOptions;

// Styled Components
const StyledSpace = styled(Space)`
  --tooltip: var(--color-text);
  width: 320px;
  display: flex;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; /* Distribute space between elements */
  gap: 6px;
`;

const RadioGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const Wrapper = styled.div`
  width: 340px;
  max-width: 380px;
  background: var(--normalize-section-bg, #ffffff);
  font-weight: 500;
  padding: 10px;
`;

const CopyButton = styled(Button)`
  padding: 4px;
  min-width: unset;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  .mdi-icon {
    display: block;
    margin: auto;
  }
`;
